/**
 * Parameter Utilities
 * 
 * This file provides utility functions for parameter validation, extraction,
 * and processing. These utilities help ensure consistent parameter handling
 * across the application.
 */

import {
  RESOURCE_TYPES,
  RESOURCE_ID_PARAMS,
  PARAM_TO_RESOURCE_TYPE,
  PARAMETER_SCHEMA,
  DEFAULT_ERROR_MESSAGES,
  updateParamToResourceTypeWithAliases
} from '../schemas/parameterSchema';

/**
 * Validate a UUID
 * 
 * @param {string} id - The ID to validate
 * @returns {boolean} True if the ID is a valid UUID, false otherwise
 */
export const isValidUuid = (id) => {
  return typeof id === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
};

/**
 * Resource ID Parameter Aliases
 * 
 * These are alternative parameter names that can be used for each resource type.
 * This helps maintain backward compatibility and handle variations in parameter naming.
 */
export const RESOURCE_ID_PARAM_ALIASES = {
  [RESOURCE_TYPES.ACTOR]: ['actorUserId'], // Alias for actorId
  // Add more aliases for other resource types as needed
};

/**
 * Memoized version of isValidUuid for performance optimization
 * Uses a simple cache to avoid re-validating the same UUIDs
 */
export const memoizedIsValidUuid = (() => {
  const cache = new Map();

  return (id) => {
    if (cache.has(id)) {
      return cache.get(id);
    }

    const result = isValidUuid(id);
    cache.set(id, result);

    // Limit cache size to prevent memory leaks
    if (cache.size > 1000) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    return result;
  };
})();

/**
 * Initialize parameter aliases
 * This function updates the PARAM_TO_RESOURCE_TYPE map with the aliases defined in RESOURCE_ID_PARAM_ALIASES
 */
export const initializeParameterAliases = () => {
  updateParamToResourceTypeWithAliases(RESOURCE_ID_PARAM_ALIASES);
  console.log('Parameter aliases initialized');
};

// Initialize parameter aliases immediately
initializeParameterAliases();

/**
 * Extract a resource ID from various parameter formats
 * 
 * @param {string|Object} param - The parameter to extract the ID from
 * @param {string} resourceType - The type of resource (e.g., 'character')
 * @returns {string|null} The extracted ID, or null if not found
 */
export const extractResourceId = (param, resourceType) => {
  // Get the parameter name for this resource type
  const idParam = RESOURCE_ID_PARAMS[resourceType];

  // Get any aliases for this resource type
  const aliases = RESOURCE_ID_PARAM_ALIASES[resourceType] || [];

  if (!idParam) {
    console.error(`Unknown resource type: ${resourceType}`);
    return null;
  }

  // If param is a string, validate it as UUID
  if (typeof param === 'string') {
    return memoizedIsValidUuid(param) ? param : null;
  }

  // If param is an object
  if (param && typeof param === 'object') {
    // Check for the specific ID parameter
    if (param[idParam] && memoizedIsValidUuid(param[idParam])) {
      return param[idParam];
    }

    // Check for aliases
    for (const alias of aliases) {
      if (param[alias] && memoizedIsValidUuid(param[alias])) {
        return param[alias];
      }
    }

    // Check for generic id/uuid properties
    if (param.id && memoizedIsValidUuid(param.id)) {
      return param.id;
    }

    if (param.uuid && memoizedIsValidUuid(param.uuid)) {
      return param.uuid;
    }
  }

  return null;
};

/**
 * Extract multiple resource IDs from a parameters object
 * 
 * @param {Object} params - The parameters object
 * @returns {Object} An object with extracted and validated IDs
 */
export const extractResourceIds = (params) => {
  if (!params || typeof params !== 'object') {
    return {};
  }

  const result = {};

  Object.entries(params).forEach(([key, value]) => {
    const resourceType = PARAM_TO_RESOURCE_TYPE[key];
    if (resourceType) {
      result[key] = extractResourceId(value, resourceType);
    } else {
      result[key] = value;
    }
  });

  return result;
};

/**
 * Validate parameters against a schema
 * 
 * @param {Object} params - The parameters to validate
 * @param {Object} schema - The schema to validate against
 * @returns {Object} An object with validation results
 */
export const validateParameters = (params, schema) => {
  const errors = {};
  const validParams = {};

  if (!params || typeof params !== 'object') {
    return {
      isValid: false,
      errors: { _general: 'Parameters must be an object' },
      validParams: {}
    };
  }

  if (!schema || typeof schema !== 'object') {
    return {
      isValid: true,
      errors: {},
      validParams: { ...params }
    };
  }

  Object.entries(schema).forEach(([key, def]) => {
    const value = params[key];

    // Check required parameters
    if (def.required && (value === undefined || value === null)) {
      errors[key] = `${key} is required`;
      return;
    }

    // If value is provided, validate type and format
    if (value !== undefined && value !== null) {
      // Type validation
      if (def.type === 'string' && typeof value !== 'string') {
        errors[key] = `${key} must be a string`;
      } else if (def.type === 'number' && typeof value !== 'number') {
        errors[key] = `${key} must be a number`;
      } else if (def.type === 'boolean' && typeof value !== 'boolean') {
        errors[key] = `${key} must be a boolean`;
      } else if (def.type === 'object' && (typeof value !== 'object' || Array.isArray(value))) {
        errors[key] = `${key} must be an object`;
      } else if (def.type === 'array' && !Array.isArray(value)) {
        errors[key] = `${key} must be an array`;
      }

      // Format validation
      if (def.format === 'uuid' && !memoizedIsValidUuid(value)) {
        errors[key] = `${key} must be a valid UUID`;
      } else if (def.format === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors[key] = `${key} must be a valid email address`;
      } else if (def.format === 'date' && isNaN(Date.parse(value))) {
        errors[key] = `${key} must be a valid date`;
      }

      // Enum validation
      if (def.enum && !def.enum.includes(value)) {
        errors[key] = `${key} must be one of: ${def.enum.join(', ')}`;
      }

      // Add to valid params if no errors
      if (!errors[key]) {
        validParams[key] = value;
      }
    } else if (def.default !== undefined) {
      // Use default value if provided
      validParams[key] = def.default;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    validParams
  };
};

/**
 * Validate a resource ID parameter
 * 
 * @param {string|Object} param - The parameter to validate
 * @param {string} resourceType - The type of resource
 * @returns {Object} An object with validation results
 */
export const validateResourceId = (param, resourceType) => {
  const idParam = RESOURCE_ID_PARAMS[resourceType];

  if (!idParam) {
    return {
      isValid: false,
      error: `Unknown resource type: ${resourceType}`,
      id: null
    };
  }

  const id = extractResourceId(param, resourceType);

  if (!id) {
    return {
      isValid: false,
      error: DEFAULT_ERROR_MESSAGES.MISSING_ID(resourceType),
      id: null
    };
  }

  return {
    isValid: true,
    error: null,
    id
  };
};

/**
 * Get the parameter name for a resource type
 * 
 * @param {string} resourceType - The type of resource
 * @returns {string|null} The parameter name, or null if not found
 */
export const getResourceIdParam = (resourceType) => {
  return RESOURCE_ID_PARAMS[resourceType] || null;
};

/**
 * Get the resource type for a parameter name
 * 
 * @param {string} paramName - The parameter name
 * @returns {string|null} The resource type, or null if not found
 */
export const getResourceTypeFromParam = (paramName) => {
  return PARAM_TO_RESOURCE_TYPE[paramName] || null;
};

/**
 * Sanitize parameters for an API request
 * 
 * @param {Object} params - The parameters to sanitize
 * @param {Object} paramMapping - Optional mapping of parameter names to route parameter names
 * @returns {Object} The sanitized parameters
 */
export const sanitizeParams = (params, paramMapping = null) => {
  if (!params || typeof params !== 'object') {
    return {};
  }

  const sanitized = {};

  Object.entries(params).forEach(([key, value]) => {
    const resourceType = PARAM_TO_RESOURCE_TYPE[key];

    if (resourceType) {
      // This is a resource ID parameter
      const id = extractResourceId(value, resourceType);
      if (id) {
        // If we have a parameter mapping, use it to map the parameter name
        const mappedKey = paramMapping && paramMapping[key] ? paramMapping[key] : key;

        // For actor resources, always map actorUserId to actorId for API routes
        const finalKey = (resourceType === RESOURCE_TYPES.ACTOR && key === 'actorUserId')
          ? RESOURCE_ID_PARAMS[RESOURCE_TYPES.ACTOR]
          : mappedKey;

        sanitized[finalKey] = id;
      }
    } else {
      // Check if this is an alias parameter
      let isAlias = false;
      for (const [type, aliases] of Object.entries(RESOURCE_ID_PARAM_ALIASES)) {
        if (aliases.includes(key)) {
          // This is an alias parameter
          isAlias = true;
          const id = extractResourceId(value, type);
          if (id) {
            // For actor resources, always map actorUserId to actorId for API routes
            const finalKey = (type === RESOURCE_TYPES.ACTOR && key === 'actorUserId')
              ? RESOURCE_ID_PARAMS[RESOURCE_TYPES.ACTOR]
              : key;

            sanitized[finalKey] = id;
          }
          break;
        }
      }

      // If not a resource ID or alias, treat as a regular parameter
      if (!isAlias) {
        sanitized[key] = value;
      }
    }
  });

  return sanitized;
};

/**
 * Extract data from a parameter object
 * 
 * @param {Object} param - The parameter object
 * @param {string} resourceType - The type of resource
 * @returns {Object} The extracted data
 */
export const extractData = (param, resourceType) => {
  if (!param || typeof param !== 'object') {
    return {};
  }

  // Check for data property
  if (param.data && typeof param.data === 'object') {
    return param.data;
  }

  // Check for resourceType + Data property (e.g., characterData)
  const dataKey = `${resourceType}Data`;
  if (param[dataKey] && typeof param[dataKey] === 'object') {
    return param[dataKey];
  }

  // Remove known ID parameters and return the rest as data
  const result = { ...param };

  // Remove standard ID parameters
  Object.values(RESOURCE_ID_PARAMS).forEach(idParam => {
    delete result[idParam];
  });

  // Remove alias ID parameters
  Object.values(RESOURCE_ID_PARAM_ALIASES).forEach(aliases => {
    aliases.forEach(alias => {
      delete result[alias];
    });
  });

  // Remove generic ID properties
  delete result.id;
  delete result.uuid;

  return result;
};
